// /plan/obstacles — Module 4 obstacle library. Lists obstacle_assets,
// uploads GeoJSON FeatureCollections (Polygon / MultiPolygon only), and
// previews them on a read-only map. Invalid GeoJSON surfaces the
// server's per-feature "issues[].index + reason" payload as a toast.
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Trash2, UploadCloud, Copy, Pencil } from "lucide-react";
import { toast } from "sonner";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { formatApiError } from "@/lib/formatApiError";
import { Input } from "@/components/ui/input";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import SpinnerButton from "@/components/SpinnerButton";
import PresetPublishButton from "@/components/PresetPublishButton";
import EntityMetadataDialog from "@/components/EntityMetadataDialog";
import { obstacles as obstaclesApi } from "@/api/endpoints";
import { useAuth } from "@/auth/AuthContext";
import { presetImmutabilityToast } from "@/lib/presetImmutability";

const MapEditor = lazy(() => import("@/components/terrain/MapEditor"));

const SCOPES = [
    { id: "all", label: "All sources" },
    { id: "mine", label: "Mine" },
    { id: "preset", label: "Presets" },
];

export default function ObstaclesPage() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);
    const [scope, setScope] = useState("all");
    const [uploading, setUploading] = useState(false);
    const [name, setName] = useState("my_obstacles");
    const [heightField, setHeightField] = useState("height_m");
    const [selectedId, setSelectedId] = useState(null);
    const [busyRowId, setBusyRowId] = useState(null);
    const [editTarget, setEditTarget] = useState(null);
    const fileInputRef = useRef(null);
    const { user } = useAuth();

    const load = useCallback(async () => {
        setLoading(true);
        setErr(null);
        try {
            const list = await obstaclesApi.list({ scope });
            setItems(Array.isArray(list) ? list : []);
        } catch (e) {
            setErr(formatApiError(e, "Failed to load obstacles"));
        } finally {
            setLoading(false);
        }
    }, [scope]);

    useEffect(() => {
        load();
    }, [load]);

    const selected = useMemo(
        () => items.find((x) => x.id === selectedId) || null,
        [items, selectedId],
    );

    const centre = useMemo(() => {
        if (!selected?.bbox) return [51.51, 0.0];
        return [
            (selected.bbox.lat_min + selected.bbox.lat_max) / 2,
            (selected.bbox.lon_min + selected.bbox.lon_max) / 2,
        ];
    }, [selected]);

    const onUpload = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            fd.append("name", name || f.name.replace(/\.[^.]+$/, ""));
            fd.append("height_field", heightField || "height_m");
            const created = await obstaclesApi.upload(fd);
            toast.success(
                `Uploaded "${created.name}" with ${created.feature_count} feature(s)`,
            );
            await load();
            setSelectedId(created.id);
        } catch (ex) {
            const detail = ex?.response?.data?.detail;
            if (detail && typeof detail === "object" && Array.isArray(detail.issues)) {
                const first = detail.issues[0];
                toast.error(
                    `Invalid GeoJSON — feature #${first.index}: ${first.reason}`,
                );
            } else {
                toast.error(
                    typeof detail === "string"
                        ? detail
                        : ex?.message || "Upload failed",
                );
            }
        } finally {
            setUploading(false);
            if (fileInputRef.current) fileInputRef.current.value = "";
        }
    };

    const onDelete = async (id) => {
        if (!window.confirm("Delete this obstacle pack?")) return;
        try {
            await obstaclesApi.remove(id);
            toast.success("Obstacle pack deleted");
            if (selectedId === id) setSelectedId(null);
            await load();
        } catch (ex) {
            toast.error(formatApiError(ex, "Delete failed"));
        }
    };

    // Phase 6 — atomic clone (GeoJSON shared; metadata wrapper forked).
    const onClone = async (id) => {
        setBusyRowId(id);
        try {
            const cloned = await obstaclesApi.clone(id);
            toast.success(`Cloned as "${cloned.name}"`);
            await load();
            setSelectedId(cloned.id);
        } catch (ex) {
            toast.error(formatApiError(ex, "Clone failed"));
        } finally {
            setBusyRowId(null);
        }
    };

    // Phase 6 — metadata-only PATCH.
    // Phase 7a — 409 immutability now offers inline `Clone now`.
    const onSaveMetadata = async (patch) => {
        if (!editTarget) return;
        try {
            await obstaclesApi.patch(editTarget.id, patch);
            toast.success("Metadata updated");
            await load();
        } catch (ex) {
            presetImmutabilityToast(ex, {
                entityType: "obstacles",
                id: editTarget.id,
                onCloned: async (cloned) => {
                    await load();
                    setSelectedId(cloned.id);
                },
            });
            throw ex;
        }
    };

    return (
        <div className="space-y-6" data-testid="obstacles-page">
            <PageHeader
                title="Obstacles"
                subtitle="GeoJSON obstacle packs — polygons with per-feature height_m for RF line-of-sight."
                actions={
                    <Select value={scope} onValueChange={setScope}>
                        <SelectTrigger
                            className="w-[150px] bg-ux-bg-2 text-[12px] font-mono"
                            data-testid="obstacles-scope"
                        >
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-ux-bg-1 border-ux-border-1">
                            {SCOPES.map((s) => (
                                <SelectItem
                                    key={s.id}
                                    value={s.id}
                                    className="text-[12px] font-mono"
                                >
                                    {s.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                }
            />

            <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                <div
                    className="lg:col-span-2 border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 space-y-2"
                    data-testid="obstacles-list"
                >
                    <div className="flex items-center justify-between">
                        <h3 className="font-mono text-xs uppercase tracking-wider text-ux-text-3">
                            {items.length} pack{items.length === 1 ? "" : "s"}
                        </h3>
                        <Link
                            to="/plan/terrain"
                            className="text-xs text-ux-accent underline decoration-dotted"
                        >
                            ← Terrain
                        </Link>
                    </div>
                    {loading ? (
                        <div className="text-xs text-ux-text-3">Loading…</div>
                    ) : err ? (
                        <div
                            className="text-xs text-ux-danger"
                            data-testid="obstacles-list-error"
                        >
                            {err}
                        </div>
                    ) : items.length === 0 ? (
                        <EmptyState
                            title="No obstacle packs yet"
                            description="Drop a GeoJSON FeatureCollection to upload."
                        />
                    ) : (
                        <ul className="space-y-1">
                            {items.map((it) => {
                                const active = it.id === selectedId;
                                return (
                                    <li
                                        key={it.id}
                                        className={`group flex items-start gap-2 px-2 py-2 rounded-md border ${
                                            active
                                                ? "border-ux-accent/60 bg-ux-accent/5"
                                                : "border-ux-border-1 hover:border-ux-border-2"
                                        }`}
                                        data-testid={`obstacle-row-${it.id}`}
                                    >
                                        <button
                                            type="button"
                                            className="flex-1 text-left"
                                            onClick={() => setSelectedId(it.id)}
                                            data-testid={`obstacle-select-${it.id}`}
                                        >
                                            <div className="flex items-center gap-2 text-sm">
                                                <span className="font-medium">{it.name}</span>
                                                {it.is_preset ? (
                                                    <span className="text-[10px] font-mono uppercase tracking-wider text-ux-text-3">
                                                        preset
                                                    </span>
                                                ) : null}
                                            </div>
                                            <div className="text-[11px] font-mono text-ux-text-3">
                                                {it.feature_count} feature
                                                {it.feature_count === 1 ? "" : "s"} ·{" "}
                                                {it.height_field || "height_m"}
                                            </div>
                                            {it.bbox ? (
                                                <div className="text-[10px] font-mono text-ux-text-3">
                                                    bbox [{it.bbox.lon_min?.toFixed(3)},{" "}
                                                    {it.bbox.lat_min?.toFixed(3)}] → [
                                                    {it.bbox.lon_max?.toFixed(3)},{" "}
                                                    {it.bbox.lat_max?.toFixed(3)}]
                                                </div>
                                            ) : null}
                                        </button>
                                        {!it.is_preset ? (
                                            <button
                                                type="button"
                                                onClick={() => setEditTarget(it)}
                                                className="text-ux-text-3 hover:text-ux-accent"
                                                data-testid={`obstacle-edit-${it.id}`}
                                                aria-label="Edit metadata"
                                                title="Edit metadata"
                                            >
                                                <Pencil className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                        <button
                                            type="button"
                                            onClick={() => onClone(it.id)}
                                            className="text-ux-text-3 hover:text-ux-accent disabled:opacity-50"
                                            data-testid={`obstacle-clone-${it.id}`}
                                            aria-label="Clone to my tenant"
                                            title="Clone to my tenant"
                                            disabled={busyRowId === it.id}
                                        >
                                            <Copy className="h-3.5 w-3.5" />
                                        </button>
                                        <PresetPublishButton
                                            entityType="obstacles"
                                            entityId={it.id}
                                            entity={it}
                                            user={user}
                                            size="sm"
                                            variant="ghost"
                                            label=""
                                            testId={`obstacle-publish-${it.id}`}
                                            onPublished={load}
                                        />
                                        {!it.is_preset ? (
                                            <button
                                                type="button"
                                                onClick={() => onDelete(it.id)}
                                                className="text-ux-text-3 hover:text-ux-danger"
                                                data-testid={`obstacle-delete-${it.id}`}
                                                aria-label="Delete"
                                                title="Delete"
                                            >
                                                <Trash2 className="h-3.5 w-3.5" />
                                            </button>
                                        ) : null}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                <div className="lg:col-span-3 space-y-3">
                    <div
                        className="border border-ux-border-1 rounded-md bg-ux-bg-1 p-3 flex flex-wrap items-end gap-3"
                        data-testid="obstacles-actions"
                    >
                        <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                            Pack name
                            <Input
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="w-48 h-8 text-[12px] font-mono"
                                data-testid="obstacles-name-input"
                            />
                        </label>
                        <label className="text-[11px] font-mono uppercase tracking-wider text-ux-text-3 space-y-1">
                            Height field
                            <Input
                                value={heightField}
                                onChange={(e) => setHeightField(e.target.value)}
                                className="w-36 h-8 text-[12px] font-mono"
                                data-testid="obstacles-height-field"
                            />
                        </label>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept=".geojson,.json,application/geo+json,application/json"
                            onChange={onUpload}
                            className="hidden"
                            data-testid="obstacles-file-input"
                        />
                        <SpinnerButton
                            size="sm"
                            loading={uploading}
                            onClick={() => fileInputRef.current?.click()}
                            testId="obstacles-upload-btn"
                        >
                            <UploadCloud className="h-3.5 w-3.5 mr-1" />
                            Upload GeoJSON
                        </SpinnerButton>
                    </div>

                    <Suspense
                        fallback={
                            <div
                                data-testid="obstacles-map-loading"
                                className="h-[420px] rounded-md border border-slate-200 bg-slate-50 flex items-center justify-center text-xs text-slate-500"
                            >
                                Loading map…
                            </div>
                        }
                    >
                        <MapEditor
                            centre={centre}
                            zoom={selected ? 13 : 4}
                            obstacles={selected ? [selected] : []}
                            heightPx={420}
                            testId="obstacles-map"
                        />
                    </Suspense>
                </div>
            </div>

            <EntityMetadataDialog
                open={!!editTarget}
                onOpenChange={(o) => !o && setEditTarget(null)}
                entity={editTarget}
                entityType="obstacle"
                onSave={onSaveMetadata}
                testId="obstacle-metadata-dialog"
            />
        </div>
    );
}
